// src/components/Admin/Cantieri/MaterialiVariabiliManagement.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../../firebase';
import './MasterDataManagement.css';
import './MaterialiVariabiliManagement.css';

const MaterialiVariabiliManagement = () => {
  const [materiali, setMateriali] = useState([]);
  const [filteredMateriali, setFilteredMateriali] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [formData, setFormData] = useState({
    nomeMacrovoce: ''
  });

  // Lista macrovoci iniziali come specificato
  const macrovoceIniziali = [
    'MATERIALE DI CONSUMO',
    'SERVIZI DI TERZI',
    'NOLEGGIO DI TERZI',
    'CARBURANTE GASOLIO',
    'CARBURANTE BENZINA',
    'MATERIE PRIME',
    'COSTI INDIRETTI',
    'ATTREZZATURE',
    'MATERIALE PROVENIENTE DA MAGAZZINO INTERNO'
  ];

  // Caricamento materiali da Firebase
  const loadMateriali = async () => {
    try {
      setIsLoading(true);
      const materialiQuery = query(
        collection(db, 'materialiVariabili'),
        orderBy('nomeMacrovoce', 'asc')
      );
      
      const snapshot = await getDocs(materialiQuery);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMateriali(data);
      console.log(`Caricati ${data.length} materiali a costo variabile`);
    } catch (error) {
      console.error("Errore nel caricamento materiali:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Importazione massiva delle macrovoci iniziali
  const importMacrovoceIniziali = async () => {
    try {
      setIsLoading(true);
      console.log('Avvio importazione macrovoci materiali a costo variabile...');
      
      const batch = [];
      for (const nomeMacrovoce of macrovoceIniziali) {
        const materialeData = {
          nomeMacrovoce: nomeMacrovoce,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        batch.push(addDoc(collection(db, 'materialiVariabili'), materialeData));
      }
      
      await Promise.all(batch);
      console.log(`Importate ${macrovoceIniziali.length} macrovoci materiali a costo variabile`);
      
      // Ricarica i dati
      await loadMateriali();
      setShowBulkImport(false);
    } catch (error) {
      console.error("Errore nell'importazione macrovoci:", error);
      alert('Errore durante l\'importazione delle macrovoci');
    } finally {
      setIsLoading(false);
    }
  };

  // Applicazione filtri
  const applyFilters = () => {
    let filtered = [...materiali];

    // Filtro per ricerca testuale
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(materiale => 
        materiale.nomeMacrovoce.toLowerCase().includes(search)
      );
    }

    setFilteredMateriali(filtered);
  };

  // Reset del form
  const resetForm = () => {
    setFormData({
      nomeMacrovoce: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Gestione input form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Salvataggio materiale
  const saveMateriale = async (e) => {
    e.preventDefault();
    
    if (!formData.nomeMacrovoce.trim()) {
      alert('Il nome della macrovoce è obbligatorio');
      return;
    }

    try {
      setIsLoading(true);
      
      const materialeData = {
        nomeMacrovoce: formData.nomeMacrovoce.trim().toUpperCase(), // Convertito in maiuscolo per coerenza
        updatedAt: new Date()
      };

      if (editingId) {
        // Aggiornamento
        const materialeRef = doc(db, 'materialiVariabili', editingId);
        await updateDoc(materialeRef, materialeData);
        console.log('Macrovoce aggiornata:', editingId);
      } else {
        // Creazione
        materialeData.createdAt = new Date();
        await addDoc(collection(db, 'materialiVariabili'), materialeData);
        console.log('Nuova macrovoce creata');
      }

      resetForm();
      await loadMateriali();
    } catch (error) {
      console.error("Errore nel salvataggio macrovoce:", error);
      alert('Errore nel salvataggio della macrovoce');
    } finally {
      setIsLoading(false);
    }
  };

  // Modifica materiale
  const editMateriale = (materiale) => {
    setFormData({
      nomeMacrovoce: materiale.nomeMacrovoce
    });
    setEditingId(materiale.id);
    setShowForm(true);
  };

  // Eliminazione materiale
  const deleteMateriale = async (materialeId, nomeMacrovoce) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la macrovoce "${nomeMacrovoce}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      const materialeRef = doc(db, 'materialiVariabili', materialeId);
      await deleteDoc(materialeRef);
      console.log('Macrovoce eliminata:', materialeId);
      await loadMateriali();
    } catch (error) {
      console.error("Errore nell'eliminazione macrovoce:", error);
      alert('Errore nell\'eliminazione della macrovoce');
    } finally {
      setIsLoading(false);
    }
  };

  // Caricamento iniziale
  useEffect(() => {
    loadMateriali();
  }, []);

  // Applicazione filtri quando cambiano i dati o i filtri
  useEffect(() => {
    applyFilters();
  }, [materiali, searchTerm]);

  if (isLoading && materiali.length === 0) {
    return (
      <div className="master-data-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Caricamento materiali a costo variabile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-data-container">
      <div className="master-data-header">
        <div className="header-left">
          <h2>📦 Gestione Materiali Costo Variabile</h2>
          <p className="header-description">
            Gestisci le macrovoci di materiali a costo variabile per la categorizzazione nei cantieri
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
            disabled={isLoading}
          >
            <span>➕</span>
            Nuova Macrovoce
          </button>
          {materiali.length === 0 && (
            <button 
              className="btn btn-secondary"
              onClick={() => setShowBulkImport(true)}
              disabled={isLoading}
            >
              <span>📥</span>
              Importa Lista
            </button>
          )}
        </div>
      </div>

      {/* Sezione filtri e ricerca */}
      <div className="master-data-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Cerca macrovoci..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-info">
          <span className="results-count">
            {filteredMateriali.length} di {materiali.length} macrovoci
          </span>
        </div>
      </div>

      {/* Lista materiali */}
      <div className="master-data-content">
        {filteredMateriali.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>Nessuna macrovoce trovata</h3>
            <p>
              {materiali.length === 0 
                ? 'Non ci sono ancora macrovoci di materiali a costo variabile. Inizia aggiungendone una.'
                : 'Nessuna macrovoce corrisponde ai filtri di ricerca.'
              }
            </p>
            {materiali.length === 0 && (
              <button 
                className="btn btn-primary"
                onClick={() => setShowForm(true)}
              >
                <span>➕</span>
                Aggiungi Prima Macrovoce
              </button>
            )}
          </div>
        ) : (
          <div className="master-data-table">
            <table>
              <thead>
                <tr>
                  <th>Nome Macrovoce</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredMateriali.map(materiale => (
                  <tr key={materiale.id}>
                    <td>
                      <div className="macrovoce-info">
                        <strong className="macrovoce-name">{materiale.nomeMacrovoce}</strong>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => editMateriale(materiale)}
                          title="Modifica macrovoce"
                          disabled={isLoading}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => deleteMateriale(materiale.id, materiale.nomeMacrovoce)}
                          title="Elimina macrovoce"
                          disabled={isLoading}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form di inserimento/modifica */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {editingId ? '✏️ Modifica Macrovoce' : '➕ Nuova Macrovoce'}
              </h3>
              <button 
                className="modal-close"
                onClick={resetForm}
                type="button"
              >
                ✕
              </button>
            </div>

            <form onSubmit={saveMateriale}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome Macrovoce *</label>
                  <input
                    type="text"
                    name="nomeMacrovoce"
                    value={formData.nomeMacrovoce}
                    onChange={handleInputChange}
                    placeholder="Es. MATERIALE DI CONSUMO"
                    required
                    disabled={isLoading}
                  />
                  <small className="form-help">
                    Inserisci il nome della categoria di materiali a costo variabile
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Salvataggio...' : (editingId ? 'Aggiorna' : 'Salva')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal di importazione massiva */}
      {showBulkImport && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>📥 Importa Lista Macrovoci</h3>
              <button 
                className="modal-close"
                onClick={() => setShowBulkImport(false)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="import-preview">
                <p>Verranno importate le seguenti {macrovoceIniziali.length} macrovoci:</p>
                
                <div className="import-list">
                  {macrovoceIniziali.map((macrovoce, index) => (
                    <div key={index} className="import-item">
                      <strong>{macrovoce}</strong>
                      <span className="import-type">Materiale a costo variabile</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowBulkImport(false)}
                disabled={isLoading}
              >
                Annulla
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={importMacrovoceIniziali}
                disabled={isLoading}
              >
                {isLoading ? 'Importazione...' : 'Conferma Importazione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialiVariabiliManagement;